import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { products } from "../lib/products";
import { IdCard, Package, Phone, SquareUser, UserRoundPlus } from "lucide-react";
import { indicateSchema, type IndicateFormValues } from "../lib/formValidation";
import { useParams, useNavigate } from "react-router";
import { formatPayload } from "@/lib/formatters";
import { Checkbox } from "./ui/checkbox";

export function IndicateForm() {
  const [formData, setFormData] = useState<IndicateFormValues>({
    name: "",
    cpf: "",
    phone: "",
    produto: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [checkTerms, setCheckTerms] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const { indicatorEmail } = useParams();

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = indicateSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      const formattedErrors: Record<string, string> = {};

      Object.entries(fieldErrors).forEach(([key, value]) => {
        if (value && value.length > 0) {
          formattedErrors[key] = value[0] ?? "";
        }
      });

      setErrors(formattedErrors);
      return;
    }

    setErrors({});
    setApiError(null);
    setIsSubmitting(true);

    const payload = formatPayload(formData, indicatorEmail);

    try {
      const response = await fetch(`/api/${indicatorEmail}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        const message = data?.message ?? `Erro ${response.status}: não foi possível enviar a indicação, tente novamente mais tarde!`
        setApiError(message);
        setIsSubmitting(false);
        return;
      }

      setTimeout(() => {
        setSubmitted(true);
        setIsSubmitting(false);
      }, 1000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setApiError("Falha na conexão. Verifique sua internet e tente novamente.");
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (submitted) {
      setFormData({
        name: "",
        cpf: "",
        phone: "",
        produto: "",
      });
      navigate(`/thanks/${indicatorEmail}`);
    }
  }, [submitted, indicatorEmail, navigate]);

  return (
    <div className="w-full bg-card p-6 rounded-xl border shadow-lg">
      <form onSubmit={handleSubmit} className="space-y-4 text-black">
        <div className="space-y-2 text-left">
          <div className="flex items-center gap-2 pb-4 text-lg font-bold text-purple-500">
            <UserRoundPlus className="bg-purple-100 p-2 rounded-lg" size={50} />
            Dados do indicado
          </div>
          <Label htmlFor="name">Nome completo</Label>
          <div className="relative">
            <SquareUser className="absolute top-1/2 left-2 transform -translate-y-1/2 w-4 h-4 text-gray-500" size={24} />
            <Input
              className="pl-9"
              id="name"
              placeholder="Ex: João Silva"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        <div className="space-y-2 text-left">
          <Label htmlFor="cpf">CPF</Label>
          <div className="relative">
            <IdCard className="absolute top-1/2 left-2 transform -translate-y-1/2 w-4 h-4 text-gray-500" size={24} />
            <Input
              className="pl-9"
              id="cpf"
              type="text"
              placeholder="000.000.000-00"
              required
              value={formData.cpf}
              onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
            />
          </div>
          {errors.cpf && <p className="text-red-500 text-xs mt-1">{errors.cpf}</p>}
        </div>

        <div className="space-y-2 text-left">
          <Label htmlFor="phone">Celular</Label>
          <div className="relative">
            <Phone className="absolute top-1/2 left-2 transform -translate-y-1/2 w-4 h-4 text-gray-500" size={24} />
            <Input
              className="pl-9"
              id="phone"
              placeholder="(21) 99999-9999"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
        </div>

        <div className="space-y-2 text-left">
          <Label htmlFor="role">Tenho interesse em</Label>
          <div className="relative">
            <Package className="absolute top-1/2 left-2 transform -translate-y-1/2 w-4 h-4 text-gray-500" size={24} />
            <Select
              value={formData.produto}
              onValueChange={(e) => setFormData({ ...formData, produto: e })}
            >
              <SelectTrigger className="w-full pl-9">
                <SelectValue placeholder="Selecione o produto" />
              </SelectTrigger>
              <SelectContent>
                {products.map((product) => (
                  <SelectItem key={product.id} value={product.name}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {errors.produto && <p className="text-red-500 text-xs mt-1">{errors.produto}</p>}
        </div>

        <div className="flex items-center gap-2">
          <Checkbox className="text-purple-500" id="privacyTerms" onCheckedChange={(checked) => setCheckTerms(!!checked)} />
          <label
            htmlFor="privacyTerms"
            className="text-sm text-muted-foreground"
          >
            Li e concordo com a <a href="https://novaisp.com.br/privacidade" target="_blank" rel="noopener noreferrer" className="text-purple-500">política de privacidade</a> da Nova ISP.
          </label>
        </div>

        {apiError && <p className="text-red-500 text-xs mt-1">{apiError}</p>}

        <Button className="w-full mt-6 bg-purple-500 hover:bg-purple-600 text-white font-bold cursor-pointer" disabled={isSubmitting || !checkTerms}>
          {isSubmitting ? "Enviando indicação..." : "Enviar Indicação"}
        </Button>
      </form>
    </div>
  );
}